'use client'

import { Fragment, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChevronDown, ChevronRight, Package, Search } from 'lucide-react'
import Link from 'next/link'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { Consignment } from '@/lib/api/types'

interface ConsignmentListProps {
  consignments: Consignment[] | null
}

const getStatusBadge = (status: string) => {
  const variants: Record<string, { variant: 'default' | 'secondary' | 'outline'; label: string }> = {
    pending: { variant: 'default', label: 'Pending' },
    completed: { variant: 'secondary', label: 'Completed' },
    processing: { variant: 'outline', label: 'Processing' },
    cancelled: { variant: 'outline', label: 'Cancelled' },
    DRAFT: { variant: 'default', label: 'Draft' },
    AWAITING_INVOICE_FROM_FORWARDER: { variant: 'outline', label: 'Awaiting Invoice' },
    AWAITING_PAYMENT: { variant: 'outline', label: 'Awaiting Payment' },
    PAYMENT_CONFIRMED: { variant: 'secondary', label: 'Payment Confirmed' },
    IN_TRANSIT_DOMESTIC: { variant: 'outline', label: 'Domestic Transit' },
    IN_TRANSIT_INTERNATIONAL: { variant: 'outline', label: 'International Transit' },
    ARRIVED: { variant: 'secondary', label: 'Arrived' },
    CLEARING: { variant: 'outline', label: 'Clearing' },
    READY_FOR_PICKUP: { variant: 'secondary', label: 'Ready for Pickup' },
    DELIVERED: { variant: 'secondary', label: 'Delivered' },
    CLOSED: { variant: 'outline', label: 'Closed' },
  }
  return variants[status] || variants.pending
}

function formatLabel(value: string | null | undefined) {
  if (!value) return 'Not set'
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export function ConsignmentList({ consignments }: ConsignmentListProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedRows(newExpanded)
  }

  // Filter consignments based on search query
  const filteredConsignments = consignments?.filter(c => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      c.tracking_number?.toLowerCase().includes(query) ||
      c.international_tracking_number?.toLowerCase().includes(query) ||
      c.destination_port?.toLowerCase().includes(query) ||
      c.destination_address?.toLowerCase().includes(query) ||
      c.status.toLowerCase().includes(query) ||
      c.service_type?.toLowerCase().includes(query) ||
      c.inquiry_stage?.toLowerCase().includes(query) ||
      c.next_action?.toLowerCase().includes(query)
    )
  })

  if (!consignments || consignments.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Package className="h-16 w-16 text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">No consignments yet</h3>
          <p className="text-muted-foreground mb-4">Create your first consignment to get started</p>
          <Link href="/consignments/new">
            <Button>Create Consignment</Button>
          </Link>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
            <CardTitle>Search Consignments</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by tracking number, destination..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <div className="rounded-md border bg-card text-card-foreground shadow-sm">
        <div className="relative w-full overflow-auto">
          <table className="w-full caption-bottom text-sm">
            <thead className="[&_tr]:border-b">
              <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                <th className="h-12 px-4 w-[50px]"></th>
                <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Tracking #</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Service</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Status</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Date</th>
                <th className="h-12 px-4 text-right align-middle font-medium text-muted-foreground">Tax Est.</th>
              </tr>
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {filteredConsignments?.map((consignment) => (
                <Fragment key={consignment.id}>
                  <tr
                    key={consignment.id}
                    className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted cursor-pointer"
                    onClick={() => toggleRow(consignment.id)}
                  >
                    <td className="p-4 align-middle">
                      {expandedRows.has(consignment.id) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </td>
                    <td className="p-4 align-middle font-medium">
                      {consignment.international_tracking_number || consignment.tracking_number || 'No Tracking #'}
                      <div className="mt-1 max-w-xs truncate text-xs font-normal text-muted-foreground">
                        {consignment.next_action || 'No next action'}
                      </div>
                    </td>
                    <td className="p-4 align-middle">
                      <div className="flex flex-col gap-1">
                        <Badge variant="secondary" className="w-fit">{formatLabel(consignment.service_type)}</Badge>
                        <span className="text-xs text-muted-foreground">{formatLabel(consignment.inquiry_stage)}</span>
                      </div>
                    </td>
                    <td className="p-4 align-middle">
                      <Badge variant={getStatusBadge(consignment.status).variant}>
                        {getStatusBadge(consignment.status).label}
                      </Badge>
                    </td>
                    <td className="p-4 align-middle">
                      {new Date(consignment.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4 align-middle text-right">
                      {consignment.total_tax_estimate
                        ? `UGX ${consignment.total_tax_estimate.toLocaleString()}`
                        : 'N/A'}
                    </td>
                  </tr>
                  {expandedRows.has(consignment.id) && (
                    <tr className="bg-muted/50 border-b">
                      <td colSpan={6} className="p-4 align-middle">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pl-10">
                          <div>
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                              Mode
                            </span>
                            <span className="capitalize font-medium">{consignment.mode}</span>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                              Destination
                            </span>
                            <span className="font-medium">{consignment.destination_port}</span>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                              Owner
                            </span>
                            <span className="font-medium">{consignment.owner_user_id || 'Unassigned'}</span>
                          </div>
                          <div className="flex items-center justify-end">
                            <Link href={`/consignments/${consignment.id}`}>
                              <Button size="sm" variant="secondary">
                                View Full Details
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {filteredConsignments && filteredConsignments.length === 0 && (
                 <tr>
                    <td colSpan={6} className="p-4 text-center text-muted-foreground">
                        No results found for &quot;{searchQuery}&quot;
                    </td>
                 </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
